import React, { useState, useMemo } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import {
  BOMItem,
  ComponentCondition,
  ProcurementStatus,
} from '../../types/bom';
import {
  RestorationPhase,
  RESTORATION_PHASES,
  VehicleSystem,
} from '../../types/teardown';
import { calculateComponentVariance } from '../../utils/budgetCalculations';
import { ConditionBadge } from '../common/ConditionBadge';
import { MechanicalButton } from '../common/MechanicalButton';
import { AddPartModal } from './AddPartModal';
import { PartsInquiryModal } from './PartsInquiryModal';
import { HollanderLookupBar } from './HollanderLookupBar';
import {
  Search,
  Plus,
  Filter,
  ArrowUpDown,
  Edit2,
  Trash2,
  FileText,
  BookOpen,
  Receipt,
  X,
  Truck,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Layers,
} from 'lucide-react';

export interface PartsMatrixTableProps {
  initialPhaseFilter?: RestorationPhase | 'ALL';
  className?: string;
}

type SortField =
  | 'partName'
  | 'phase'
  | 'condition'
  | 'status'
  | 'estimatedCost'
  | 'actualCost'
  | 'variance';
type SortOrder = 'asc' | 'desc';

export const PartsMatrixTable: React.FC<PartsMatrixTableProps> = ({
  initialPhaseFilter = 'ALL',
  className = '',
}) => {
  const { state, dispatch } = useRestoMod();
  const { bomItems, subAssemblies } = state;

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [phaseFilter, setPhaseFilter] = useState<RestorationPhase | 'ALL'>(initialPhaseFilter);
  const [conditionFilter, setConditionFilter] = useState<ComponentCondition | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter] = useState<ProcurementStatus | 'ALL'>('ALL');
  const [systemFilter, setSystemFilter] = useState<VehicleSystem | 'ALL'>('ALL');

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('phase');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<BOMItem | null>(null);
  const [inquiryItem, setInquiryItem] = useState<BOMItem | null>(null);
  const [hollanderItemQuery, setHollanderItemQuery] = useState<string | null>(null);

  // Status toggle handler
  const handleToggleStatus = (item: BOMItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const cycleMap: Record<ProcurementStatus, ProcurementStatus> = {
      Needed: 'Sourced',
      Sourced: 'Installed',
      Installed: 'Needed',
    };
    const nextStatus = cycleMap[item.status];
    const updated: BOMItem = {
      ...item,
      status: nextStatus,
      // If moving from Needed to Sourced and actualCost was 0, default actualCost to estimatedCost
      actualCost:
        item.status === 'Needed' && item.actualCost === 0 ? item.estimatedCost : item.actualCost,
      updatedAt: new Date().toISOString(),
    };
    dispatch({ type: 'UPDATE_BOM_ITEM', payload: updated });
  };

  const handleDeleteItem = (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to remove "${name}" from the Bill of Materials?`)) {
      dispatch({ type: 'DELETE_BOM_ITEM', payload: id });
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Filtered & Sorted items
  const filteredItems = useMemo(() => {
    return bomItems.filter((item) => {
      // Phase filter
      if (phaseFilter !== 'ALL' && item.phase !== phaseFilter) return false;
      // Condition filter
      if (conditionFilter !== 'ALL' && item.condition !== conditionFilter) return false;
      // Status filter
      if (statusFilter !== 'ALL' && item.status !== statusFilter) return false;
      // System filter
      if (systemFilter !== 'ALL' && item.system !== systemFilter) return false;

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = item.partName.toLowerCase().includes(q);
        const matchesPN = item.partNumber.toLowerCase().includes(q);
        const matchesVendor = item.vendor.toLowerCase().includes(q);
        const matchesHollander = Boolean(item.hollanderNumber?.toLowerCase().includes(q));
        const matchesNotes = item.notes.toLowerCase().includes(q);
        if (!matchesName && !matchesPN && !matchesVendor && !matchesHollander && !matchesNotes) {
          return false;
        }
      }

      return true;
    });
  }, [bomItems, phaseFilter, conditionFilter, statusFilter, systemFilter, searchQuery]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'partName':
          comparison = a.partName.localeCompare(b.partName);
          break;
        case 'phase': {
          const seqA = RESTORATION_PHASES[a.phase]?.sequence || 0;
          const seqB = RESTORATION_PHASES[b.phase]?.sequence || 0;
          comparison = seqA - seqB;
          break;
        }
        case 'condition':
          comparison = a.condition.localeCompare(b.condition);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'estimatedCost':
          comparison = (a.estimatedCost * a.quantity) - (b.estimatedCost * b.quantity);
          break;
        case 'actualCost': {
          const actA = calculateComponentVariance(a).netActualCost;
          const actB = calculateComponentVariance(b).netActualCost;
          comparison = actA - actB;
          break;
        }
        case 'variance': {
          const varA = calculateComponentVariance(a).costVariance;
          const varB = calculateComponentVariance(b).costVariance;
          comparison = varA - varB;
          break;
        }
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [filteredItems, sortField, sortOrder]);

  // Aggregate stats of current filtered selection
  const filteredMetrics = useMemo(() => {
    let totalEst = 0;
    let totalAct = 0;
    let neededCount = 0;
    let sourcedCount = 0;
    let installedCount = 0;

    filteredItems.forEach((item) => {
      const v = calculateComponentVariance(item);
      totalEst += v.baseEstimatedCost;
      if (item.status === 'Needed') {
        neededCount += 1;
      } else {
        totalAct += v.netActualCost;
        if (item.status === 'Sourced') sourcedCount += 1;
        if (item.status === 'Installed') installedCount += 1;
      }
    });

    return {
      totalEst,
      totalAct,
      neededCount,
      sourcedCount,
      installedCount,
      count: filteredItems.length,
    };
  }, [filteredItems]);

  const resetFilters = () => {
    setSearchQuery('');
    setPhaseFilter('ALL');
    setConditionFilter('ALL');
    setStatusFilter('ALL');
    setSystemFilter('ALL');
  };

  const hasActiveFilters =
    searchQuery ||
    phaseFilter !== 'ALL' ||
    conditionFilter !== 'ALL' ||
    statusFilter !== 'ALL' ||
    systemFilter !== 'ALL';

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Table Toolbar & Search Bar */}
      <div className="bg-shop-darkest border border-shop-border rounded p-4 space-y-3">
        {/* Row 1: Search & Add Button */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch justify-between">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-shop-muted">
              <Search className="w-4 h-4 text-safety-amber" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search parts, part #, vendor, Hollander interchange, notes..."
              className="w-full min-h-[44px] pl-10 pr-11 py-2 bg-shop-panel border border-shop-border rounded text-xs font-mono text-shop-highlight placeholder-shop-muted focus:outline-none focus:border-safety-amber"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight cursor-pointer"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <MechanicalButton
              variant="primary"
              size="default"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => {
                setEditingItem(null);
                setIsAddModalOpen(true);
              }}
            >
              Add Component
            </MechanicalButton>
          </div>
        </div>

        {/* Row 2: 4 Filter Dropdowns */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 border-t border-shop-border text-xs font-mono">
          {/* Phase Filter */}
          <div>
            <label className="text-[10px] text-shop-muted uppercase block mb-1">
              Restoration Phase
            </label>
            <select
              value={phaseFilter}
              onChange={(e) => setPhaseFilter(e.target.value as RestorationPhase | 'ALL')}
              className="w-full min-h-[44px] px-2.5 py-1.5 bg-shop-panel border border-shop-border rounded text-shop-light focus:outline-none focus:border-safety-amber cursor-pointer"
            >
              <option value="ALL">ALL PHASES</option>
              {(Object.keys(RESTORATION_PHASES) as RestorationPhase[]).map((p) => (
                <option key={p} value={p}>
                  {RESTORATION_PHASES[p].sequence}. {RESTORATION_PHASES[p].title}
                </option>
              ))}
            </select>
          </div>

          {/* Condition Filter */}
          <div>
            <label className="text-[10px] text-shop-muted uppercase block mb-1">
              Condition
            </label>
            <select
              value={conditionFilter}
              onChange={(e) => setConditionFilter(e.target.value as ComponentCondition | 'ALL')}
              className="w-full min-h-[44px] px-2.5 py-1.5 bg-shop-panel border border-shop-border rounded text-shop-light focus:outline-none focus:border-safety-amber cursor-pointer"
            >
              <option value="ALL">ALL CONDITIONS</option>
              <option value="OEM NOS">OEM NOS</option>
              <option value="Reproduction">REPRODUCTION</option>
              <option value="Salvage">SALVAGE</option>
              <option value="Remanufactured">REMANUFACTURED</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="text-[10px] text-shop-muted uppercase block mb-1">
              Sourcing Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ProcurementStatus | 'ALL')}
              className="w-full min-h-[44px] px-2.5 py-1.5 bg-shop-panel border border-shop-border rounded text-shop-light focus:outline-none focus:border-safety-amber cursor-pointer"
            >
              <option value="ALL">ALL STATUSES</option>
              <option value="Needed">NEEDED (Pending)</option>
              <option value="Sourced">SOURCED (Committed)</option>
              <option value="Installed">INSTALLED (Torqued)</option>
            </select>
          </div>

          {/* Reset Filters / Count indicator */}
          <div className="flex flex-col justify-end">
            {hasActiveFilters ? (
              <button
                onClick={resetFilters}
                className="w-full min-h-[44px] px-3 py-1.5 rounded border border-shop-border bg-shop-panel text-safety-amber hover:text-safety-amberDark text-xs font-bold uppercase flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <Filter className="w-3.5 h-3.5" />
                Clear Filters
              </button>
            ) : (
              <div className="min-h-[44px] px-3 py-1.5 rounded border border-shop-border bg-shop-dark flex items-center justify-between text-[11px] text-shop-muted">
                <span>Total Catalog:</span>
                <span className="font-bold text-shop-highlight">{bomItems.length} parts</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Parts Table Container */}
      <div className="bg-shop-panel border border-shop-border rounded overflow-hidden shadow-tactile">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-shop-border bg-shop-darkest text-shop-muted font-mono uppercase tracking-wider select-none">
                <th
                  onClick={() => handleSort('status')}
                  className="py-3 px-3 text-center cursor-pointer hover:text-shop-highlight whitespace-nowrap"
                  title="Click to sort by status"
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>Status</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>

                <th
                  onClick={() => handleSort('partName')}
                  className="py-3 px-3 cursor-pointer hover:text-shop-highlight"
                  title="Click to sort by part description"
                >
                  <div className="flex items-center gap-1">
                    <span>Part Description &amp; Details</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>

                <th
                  onClick={() => handleSort('phase')}
                  className="py-3 px-3 cursor-pointer hover:text-shop-highlight whitespace-nowrap"
                >
                  <div className="flex items-center gap-1">
                    <span>Phase / System</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>

                <th
                  onClick={() => handleSort('condition')}
                  className="py-3 px-3 cursor-pointer hover:text-shop-highlight whitespace-nowrap"
                >
                  <div className="flex items-center gap-1">
                    <span>Condition</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>

                <th className="py-3 px-3 whitespace-nowrap">
                  <span>Hollander #</span>
                </th>

                <th
                  onClick={() => handleSort('estimatedCost')}
                  className="py-3 px-3 text-right cursor-pointer hover:text-shop-highlight whitespace-nowrap"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Est Cost</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>

                <th
                  onClick={() => handleSort('actualCost')}
                  className="py-3 px-3 text-right cursor-pointer hover:text-shop-highlight whitespace-nowrap"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Net Actual</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>

                <th
                  onClick={() => handleSort('variance')}
                  className="py-3 px-3 text-right cursor-pointer hover:text-shop-highlight whitespace-nowrap"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Variance</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>

                <th className="py-3 px-3 text-center whitespace-nowrap">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-shop-border">
              {sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-shop-muted font-mono">
                    <div className="max-w-md mx-auto space-y-3">
                      <Layers className="w-8 h-8 text-shop-steel mx-auto" />
                      <p className="text-sm">No BOM parts match the selected filters.</p>
                      <button
                        type="button"
                        onClick={resetFilters}
                        className="min-h-[44px] px-3 inline-flex items-center text-xs text-safety-amber underline hover:text-safety-amberDark cursor-pointer"
                      >
                        Reset search &amp; phase filters
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedItems.map((item) => {
                  const variance = calculateComponentVariance(item);
                  const linkedSub = item.subAssemblyId
                    ? subAssemblies.find((s) => s.id === item.subAssemblyId)
                    : null;

                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-shop-well transition-colors group select-text"
                    >
                      {/* Column 1: Procurement Status Toggle */}
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        <button
                          type="button"
                          onClick={(e) => handleToggleStatus(item, e)}
                          title="Click to cycle status: Needed -> Sourced -> Installed"
                          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center p-1 rounded hover:opacity-90 active:scale-95 transition-transform cursor-pointer"
                        >
                          <ConditionBadge status={item.status} size="sm" />
                        </button>
                      </td>

                      {/* Column 2: Part Name & Description */}
                      <td className="py-2.5 px-3">
                        <div className="font-bold text-shop-highlight uppercase tracking-wide text-xs">
                          {item.partName}
                        </div>
                        <div className="text-[11px] font-mono text-shop-muted flex items-center gap-2 flex-wrap mt-0.5">
                          {item.partNumber && (
                            <span>
                              PN: <strong className="text-shop-light">{item.partNumber}</strong>
                            </span>
                          )}
                          {item.vendor && (
                            <span>
                              Vendor: <span className="text-shop-light">{item.vendor}</span>
                            </span>
                          )}
                          {item.quantity > 1 && (
                            <span className="text-safety-amber font-bold">
                              (Qty: {item.quantity} {item.unit})
                            </span>
                          )}
                        </div>

                        {/* Extra badging: Receipts, shipping, core charge */}
                        {(item.receiptNumber || item.receiptUrl || item.coreCharge > 0 || item.shippingCost > 0) && (
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            {item.receiptNumber && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-shop-darkest border border-shop-border text-safety-green"
                                title={`Invoice / Receipt #: ${item.receiptNumber}`}
                              >
                                <Receipt className="w-3 h-3" />
                                {item.receiptNumber}
                              </span>
                            )}
                            {item.shippingCost > 0 && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-shop-darkest border border-shop-border text-shop-muted"
                                title={`Shipping: $${item.shippingCost.toFixed(2)}`}
                              >
                                <Truck className="w-3 h-3 text-shop-steel" />
                                +${item.shippingCost.toFixed(0)}
                              </span>
                            )}
                            {item.coreCharge > 0 && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-shop-darkest border border-shop-border text-safety-amber"
                                title={`Core Deposit: $${item.coreCharge.toFixed(2)} | Refunded: $${item.coreRefunded.toFixed(2)}`}
                              >
                                <RotateCcw className="w-3 h-3" />
                                Core: ${item.coreCharge.toFixed(0)}
                                {item.coreRefunded > 0 && ` (Ref: -$${item.coreRefunded.toFixed(0)})`}
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Column 3: Phase / System / Bin */}
                      <td className="py-2.5 px-3 whitespace-nowrap font-mono text-[11px]">
                        <div className="text-shop-light font-bold">
                          {RESTORATION_PHASES[item.phase]?.shortCode} • {RESTORATION_PHASES[item.phase]?.title}
                        </div>
                        <div className="text-shop-muted text-[10px] uppercase">
                          Sys: {item.system.replace('_', ' ')}
                        </div>
                        {linkedSub && (
                          <div className="text-safety-amber text-[10px] mt-0.5" title={linkedSub.name}>
                            Bin: {linkedSub.binNumber}
                          </div>
                        )}
                      </td>

                      {/* Column 4: Condition */}
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <ConditionBadge condition={item.condition} size="sm" />
                      </td>

                      {/* Column 5: Hollander Interchange */}
                      <td className="py-2.5 px-3 whitespace-nowrap font-mono text-[11px]">
                        {item.hollanderNumber ? (
                          <button
                            type="button"
                            onClick={() => setHollanderItemQuery(item.hollanderNumber || null)}
                            className="min-h-[44px] px-2.5 inline-flex items-center gap-1 rounded bg-shop-darkest border border-shop-border text-safety-amber hover:border-safety-amber transition-colors cursor-pointer"
                            title="Click to view full Hollander donor vehicle cross-reference"
                          >
                            <BookOpen className="w-3 h-3" />
                            <span>{item.hollanderNumber}</span>
                          </button>
                        ) : (
                          <span className="text-shop-muted text-[11px] font-mono">None</span>
                        )}
                      </td>

                      {/* Column 6: Estimated Cost */}
                      <td className="py-2.5 px-3 text-right font-mono text-shop-light whitespace-nowrap">
                        ${variance.baseEstimatedCost.toFixed(2)}
                      </td>

                      {/* Column 7: Net Actual Cost */}
                      <td className="py-2.5 px-3 text-right font-mono font-bold whitespace-nowrap">
                        {item.status === 'Needed' ? (
                          <span className="text-shop-muted font-normal italic">Pending</span>
                        ) : (
                          <span className="text-safety-amber">
                            ${variance.netActualCost.toFixed(2)}
                          </span>
                        )}
                      </td>

                      {/* Column 8: Line Item Cost Variance */}
                      <td className="py-2.5 px-3 text-right font-mono whitespace-nowrap">
                        {item.status === 'Needed' ? (
                          <span className="text-shop-muted text-[11px]">-</span>
                        ) : (
                          <span
                            className={`inline-flex items-center gap-1 font-bold ${
                              variance.isFavorable ? 'text-safety-green' : 'text-safety-red'
                            }`}
                          >
                            {variance.isFavorable ? (
                              <CheckCircle2 className="w-3 h-3" />
                            ) : (
                              <AlertTriangle className="w-3 h-3" />
                            )}
                            {variance.isFavorable ? '-' : '+'}
                            ${Math.abs(variance.costVariance).toFixed(2)}
                          </span>
                        )}
                      </td>

                      {/* Column 9: Actions (Edit, RFQ, Delete) */}
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          {/* Sourcing Inquiry / RFQ */}
                          <button
                            type="button"
                            onClick={() => setInquiryItem(item)}
                            title="Generate Parts Wanted RFQ / Inquiry"
                            className="min-h-[44px] min-w-[44px] p-2 rounded bg-shop-dark border border-shop-border text-shop-muted hover:text-safety-amber hover:border-safety-amber flex items-center justify-center cursor-pointer transition-colors"
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </button>

                          {/* Edit Item */}
                          <button
                            type="button"
                            onClick={() => {
                              setEditingItem(item);
                              setIsAddModalOpen(true);
                            }}
                            title="Edit BOM item parameters"
                            className="min-h-[44px] min-w-[44px] p-2 rounded bg-shop-dark border border-shop-border text-shop-muted hover:text-shop-highlight hover:border-shop-steel flex items-center justify-center cursor-pointer transition-colors"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>

                          {/* Delete Item */}
                          <button
                            type="button"
                            onClick={(e) => handleDeleteItem(item.id, item.partName, e)}
                            title="Remove from BOM"
                            className="min-h-[44px] min-w-[44px] p-2 rounded bg-shop-dark border border-shop-border text-shop-muted hover:text-safety-red hover:border-safety-red flex items-center justify-center cursor-pointer transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer Summary Bar */}
        <div className="px-4 py-3 bg-shop-darkest border-t border-shop-border flex items-center justify-between flex-wrap gap-4 text-xs font-mono">
          <div className="flex items-center gap-3 text-shop-muted">
            <span>
              Showing: <strong className="text-shop-highlight">{filteredMetrics.count}</strong> of{' '}
              {bomItems.length} parts
            </span>
            <span>•</span>
            <span className="text-safety-amber">{filteredMetrics.neededCount} Needed</span>
            <span>•</span>
            <span className="text-blueprint-cyan">{filteredMetrics.sourcedCount} Sourced</span>
            <span>•</span>
            <span className="text-safety-green">{filteredMetrics.installedCount} Installed</span>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-shop-muted">
              Filtered Est:{' '}
              <strong className="text-shop-light">${filteredMetrics.totalEst.toFixed(2)}</strong>
            </span>
            <span className="text-shop-muted">
              Filtered Actual Spent:{' '}
              <strong className="text-safety-amber font-bold">
                ${filteredMetrics.totalAct.toFixed(2)}
              </strong>
            </span>
          </div>
        </div>
      </div>

      {/* Add / Edit Part Modal */}
      {isAddModalOpen && (
        <AddPartModal
          isOpen={isAddModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingItem(null);
          }}
          initialItem={editingItem}
          initialPhase={phaseFilter !== 'ALL' ? phaseFilter : undefined}
        />
      )}

      {/* Parts Inquiry / RFQ Modal */}
      {inquiryItem && (
        <PartsInquiryModal
          isOpen={Boolean(inquiryItem)}
          onClose={() => setInquiryItem(null)}
          item={inquiryItem}
        />
      )}

      {/* Hollander Lookup Directory Modal */}
      {hollanderItemQuery && (
        <HollanderLookupBar
          isOpen={Boolean(hollanderItemQuery)}
          onClose={() => setHollanderItemQuery(null)}
          initialQuery={hollanderItemQuery}
        />
      )}
    </div>
  );
};
